// Parent payment history — /parent/billing/history.
//
// Reverse-chrono list of the caller's family's payment attempts
// (manual + autopay), grouped by calendar month. Pure reuse: no new
// backend endpoints, no new RPCs. Relies on Module D's broadened
// parent RLS on payment_transactions (migration 20270301000000).

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Receipt, CheckCircle, XCircle, ArrowLeft, CaretRight, Lightning, CreditCard,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { centsToUsd, listParentPaymentHistory, openParentInvoicePdf } from "@/lib/billing";

export default function ParentPaymentHistory() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const data = await listParentPaymentHistory({ limit: 200 });
        setRows(data);
      } catch (err) {
        toast.error(err.message || "Could not load payment history");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const byMonth = useMemo(() => groupByMonth(rows), [rows]);
  const totals = useMemo(() => {
    const succeeded = rows.filter((r) => r.status === "succeeded");
    const sum = succeeded.reduce((a, r) => a + (r.amount_cents || 0), 0);
    return { count: succeeded.length, sum };
  }, [rows]);

  const onDownload = async (invoiceId) => {
    setDownloadingId(invoiceId);
    try {
      await openParentInvoicePdf(invoiceId);
    } catch (err) {
      toast.error(err.message || "Could not open invoice PDF");
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div data-testid="parent-payment-history" className="space-y-5">
      <header>
        <Link
          to="/parent/billing"
          data-testid="parent-payment-history-back"
          className="inline-flex items-center gap-1 text-xs font-semibold text-neutral-600 hover:text-primary-hover"
        >
          <ArrowLeft size={12} weight="bold" /> Back to billing
        </Link>
        <h1 className="mt-2 font-heading text-3xl font-semibold tracking-tight text-neutral-900 sm:text-4xl">
          Payment history
        </h1>
        <p className="mt-1 max-w-md text-sm text-neutral-600">
          Every successful and failed charge on your account, newest first.
        </p>
        {!loading && totals.count > 0 && (
          <div
            data-testid="parent-payment-history-total"
            className="mt-4 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/5 px-3 py-1.5 text-xs font-semibold text-primary-hover"
          >
            <CheckCircle size={12} weight="duotone" />
            {totals.count} successful charges · {centsToUsd(totals.sum)} total
          </div>
        )}
      </header>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
        </div>
      ) : rows.length === 0 ? (
        <div
          data-testid="parent-payment-history-empty"
          className="rounded-2xl border border-dashed border-neutral-200 bg-white px-6 py-12 text-center"
        >
          <Receipt size={36} weight="duotone" className="mx-auto text-neutral-300" />
          <p className="mt-3 font-heading text-base font-semibold text-neutral-800">
            No payments yet
          </p>
          <p className="mx-auto mt-1 max-w-xs text-xs text-neutral-500">
            When an invoice is paid, a record of the transaction will appear here.
          </p>
        </div>
      ) : (
        <div className="space-y-6" data-testid="parent-payment-history-list">
          {byMonth.map(({ key, label, items }) => (
            <section
              key={key}
              data-testid={`parent-payment-history-month-${key}`}
              className="space-y-2"
            >
              <h2 className="text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500">
                {label}
              </h2>
              <ul className="space-y-2">
                {items.map((r) => (
                  <PaymentRow
                    key={r.id}
                    row={r}
                    downloading={downloadingId === r.invoice?.id}
                    onDownload={onDownload}
                  />
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

function PaymentRow({ row, downloading, onDownload }) {
  const ok = row.status === "succeeded";
  const isAutopay = row.payment_status === "autopay";
  const isSetup = row.payment_status === "autopay_setup";
  const invoice = row.invoice;
  const reason = row.metadata?.error || row.metadata?.reason || null;
  const attempt = row.metadata?.attempt;

  return (
    <li
      data-testid={`parent-payment-history-row-${row.id}`}
      data-status={row.status}
      data-kind={row.payment_status}
      className={`flex flex-wrap items-center justify-between gap-3 rounded-2xl border px-4 py-3 ${
        ok ? "border-neutral-200 bg-white" : "border-[#C6851E]/30 bg-[#C6851E]/5"
      }`}
    >
      <div className="flex min-w-0 items-start gap-3">
        <span
          className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl ${
            ok ? "bg-primary/10 text-primary-hover" : "bg-[#C6851E]/10 text-[#C6851E]"
          }`}
        >
          {ok
            ? (isAutopay ? <Lightning size={18} weight="duotone" /> : <CheckCircle size={18} weight="duotone" />)
            : <XCircle size={18} weight="duotone" />
          }
        </span>
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-neutral-900">
            {isSetup ? "Autopay set up" : invoice?.title || "Payment"}
          </p>
          <p className="mt-0.5 truncate text-[11px] text-neutral-500">
            {new Date(row.created_at).toLocaleString(undefined, {
              month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
            })}
            {isAutopay && !isSetup ? " · Autopay" : ""}
            {!isAutopay && !isSetup ? " · Manual payment" : ""}
            {attempt ? ` · attempt ${attempt}` : ""}
            {reason && !ok ? ` · ${reason}` : ""}
          </p>
        </div>
      </div>
      <div className="flex flex-shrink-0 items-center gap-2">
        <span className="font-mono text-sm text-neutral-700">
          {centsToUsd(row.amount_cents)}
        </span>
        {invoice?.id && (
          <button
            type="button"
            data-testid={`parent-payment-history-pdf-${row.id}`}
            onClick={() => onDownload(invoice.id)}
            disabled={downloading}
            title="Download invoice PDF"
            aria-label="Download invoice PDF"
            className="inline-flex items-center gap-1 rounded-full border border-neutral-200 bg-white px-2 py-1 text-[11px] font-semibold text-neutral-700 hover:bg-neutral-50 disabled:opacity-60"
          >
            {downloading ? "…" : "PDF"}
            <CaretRight size={10} weight="bold" />
          </button>
        )}
      </div>
    </li>
  );
}

function groupByMonth(rows) {
  const map = new Map();
  for (const r of rows) {
    const d = new Date(r.created_at);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleDateString(undefined, { year: "numeric", month: "long" });
    if (!map.has(key)) map.set(key, { key, label, items: [] });
    map.get(key).items.push(r);
  }
  return Array.from(map.values());
}
